import { Injectable } from '@angular/core';
import { ReportTranscriptInput } from 'src/app/infrastructure/model/ReportDto';

export interface ReportDraft {
  title: string;
  date: string;
  weather: string;
  timeFrom: string | null;
  timeTo: string | null;
  attendees: string[];
  text: string;
  transcripts: ReportTranscriptInput[];
  pendingPhotos: File[];
}

/**
 * Keeps an unsaved report in memory while the user leaves the editor to take a photo
 * with the in-app camera (`/capture`), which destroys and recreates the editor component.
 * E1 only (spec 8.7); E2 moves drafts to Dexie. FormStateService is not reused on purpose:
 * it is untyped and holds a single slot shared with the task forms.
 */
@Injectable({
  providedIn: 'root'
})
export class ReportDraftService {
  private drafts = new Map<string, ReportDraft>();

  static key(journalId: string, reportId: string | null): string {
    return `${journalId}:${reportId ?? 'new'}`;
  }

  save(key: string, draft: ReportDraft): void {
    this.drafts.set(key, {
      ...draft,
      attendees: [...draft.attendees],
      transcripts: [...draft.transcripts],
      pendingPhotos: [...draft.pendingPhotos],
    });
  }

  take(key: string): ReportDraft | null {
    const draft = this.drafts.get(key) ?? null;
    this.drafts.delete(key);

    return draft;
  }

  /** Session end on a shared device: the next user must not see drafts of the previous one. */
  clearAll(): void {
    this.drafts.clear();
  }

  clear(key: string): void {
    this.drafts.delete(key);
  }
}
